import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiteLang } from '../utils/langPath';
import { pageUrls } from '../utils/seo';

export interface PageSeo {
  /** Own description for Google / share previews (default: the site description). */
  description?: string;
  /** schema.org structured data (one object or several). */
  jsonLd?: object | object[];
  /** Picture for share previews (default: the site logo). */
  image?: string;
  /** "Not found" and similar pages: keep out of Google. */
  noindex?: boolean;
}

const DEFAULT_IMAGE = 'https://neve-shamir-community.org/logo512.png?v=3';

function setMeta(attr: 'name' | 'property', key: string, content: string | null) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (content === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(selector: string, attrs: Record<string, string> | null) {
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (attrs === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}

/**
 * Sets the page's head tags (docs/seo-plan.md): title ("<title> | Neve Shamir
 * Community"), description, the canonical address of this language copy, the
 * hreflang links to its twin, share tags and structured data. Pass undefined
 * while the page is still loading: the title is then just the site name, and
 * the page is not "ready" for the pre-render script yet.
 */
export function usePageTitle(title?: string, seo: PageSeo = {}) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang: SiteLang = i18n.language === 'en' ? 'en' : 'he';
  const siteName = t('seo.siteName');
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const description = seo.description || t('seo.desc.default');
  const jsonLd = seo.jsonLd ? JSON.stringify(seo.jsonLd) : '';
  const { image, noindex } = seo;

  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);

  useEffect(() => {
    const urls = pageUrls(pathname, lang);
    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex' : null);
    setLink('link[rel="canonical"]', noindex ? null : { rel: 'canonical', href: urls.canonical });
    setLink('link[rel="alternate"][hreflang="he"]', noindex ? null : { rel: 'alternate', hreflang: 'he', href: urls.he });
    setLink('link[rel="alternate"][hreflang="en"]', noindex ? null : { rel: 'alternate', hreflang: 'en', href: urls.en });
    setLink(
      'link[rel="alternate"][hreflang="x-default"]',
      noindex ? null : { rel: 'alternate', hreflang: 'x-default', href: urls.he }
    );
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', urls.canonical);
    setMeta('property', 'og:image', image || DEFAULT_IMAGE);
    setMeta('property', 'og:locale', lang === 'he' ? 'he_IL' : 'en_US');
    setMeta('property', 'og:locale:alternate', lang === 'he' ? 'en_US' : 'he_IL');
    setMeta('property', 'og:site_name', siteName);

    let script = document.getElementById('seo-jsonld');
    if (!jsonLd) {
      script?.remove();
    } else {
      if (!script) {
        script = document.createElement('script');
        script.id = 'seo-jsonld';
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = jsonLd;
    }
  }, [pathname, lang, fullTitle, description, image, noindex, jsonLd, siteName]);

  // The pre-render script (and the swap from a pre-rendered page) waits for this.
  useEffect(() => {
    document.documentElement.dataset.seoReady = title ? '1' : '0';
  }, [title]);
}
